import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Box,
  Tooltip,
  Divider,
  Typography,
} from "@mui/material";

import {
  ExpandLess,
  ExpandMore,
  Home,
  Business,
  Store,
  People,
  Security,
  BarChart,
  PeopleOutline,
  PeopleTwoTone,
  Diversity3,
  PinDrop,
  Source,
  AccountBalance,
  LocalShipping,
  AdminPanelSettings,
  Article,
} from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import ArticleIcon from '@mui/icons-material/Article';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';

//---------------- Auth---------------- 
import { AuthProvider, useAuth } from "./AuthContext";

const Sidebar = ({ open }) => {
  const [masterOpen, setMasterOpen] = useState(false);
  const [tranascationOpen, setTransactionOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const [subMenuOpen, setSubMenuOpen] = useState({
    purchase: false,
    sale: false,
    stocks: false,
    Accounts: false,
  });

  const toggleSubMenu = (menu) => {
    setSubMenuOpen((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const { companyName, companyId, isSuperAdmin, superCompanyId, permissions, roleName, isAppointmentNeeded } = useAuth();

  // DEBUG: Check isAppointmentNeeded value
  console.log('🔍 Sidebar - isAppointmentNeeded:', isAppointmentNeeded, 'Type:', typeof isAppointmentNeeded);

  {/* Permissions */ }
  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReadReportsPermission = permissions.includes('Read Reports');

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? 260 : 70,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? 260 : 70,
          background: "linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)",
          color: "white",
          overflowX: "hidden",
          overflowY: "auto",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "4px 0 24px rgba(0, 0, 0, 0.3)",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255, 255, 255, 0.05)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            borderRadius: "3px",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.3)",
            },
          },
        },
      }}
    >
      {/* Modern Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: open ? 3 : 2,
          justifyContent: open ? "flex-start" : "center",
          // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "20px",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            //background: "radial-gradient(circle at top right, rgba(255,255,255,0.1), transparent)",
          },
        }}
      >
        {open && (
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                letterSpacing: "0.5px",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >

            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                opacity: 0.9,
                display: "block",
                marginTop: "2px",
              }}
            >

            </Typography>
          </Box>
        )}
        {!open && (
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 700,
            }}
          >
            M
          </Typography>
        )}
      </Box>
      <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", height: "1px" }} />

      <List sx={{ marginTop: '12px', px: 1 }}>

        {/* Master Section */}
        {hasReadMasterPermission && (
          <ListItemButton
            onClick={() => setMasterOpen(!masterOpen)}
            sx={{
              borderRadius: "12px",
              mb: 0.5,
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(102, 126, 234, 0.15)",
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
              <Business sx={{
                color: "white",
                fontSize: "22px",
              }} />
            </ListItemIcon>
            {open &&
              <ListItemText
                primary="Master"
                primaryTypographyProps={{
                  style: {
                    cursor: 'pointer',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  },
                }}
              />}

            {open && (masterOpen ?
              <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
              <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
            )}
          </ListItemButton>
        )}

        <Collapse in={masterOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(102, 126, 234, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>

              <ListItemButton
                component={NavLink}
                to="/CompanyEdit"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Company"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to="/DepartmentIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Department & QA"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/ItemDashboard"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #764ba2",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Item"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/PatientIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Patient"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/SupplierIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Supplier"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/DoctorIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Doctor"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/TreatmentsIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Treatment"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

            </List>
          </Box>
        </Collapse >


        {hasReadMasterPermission && (
          <ListItemButton
            onClick={() => setTransactionOpen(!tranascationOpen)}
            sx={{
              borderRadius: "12px",
              mb: 0.5,
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(102, 126, 234, 0.15)",
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
              <AccountBalanceIcon sx={{
                color: "white",
                fontSize: "22px",
              }} />
            </ListItemIcon>
            {open &&
              <ListItemText
                primary="Services"
                primaryTypographyProps={{
                  style: {
                    cursor: 'pointer',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  },
                }}
              />}

            {open && (tranascationOpen ?
              <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
              <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
            )}
          </ListItemButton>
        )}

        <Collapse in={tranascationOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(16, 185, 129, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>

              <ListItemButton
                component={NavLink}
                to="/AppointmentIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(16, 185, 129, 0.3)",
                    borderLeft: "3px solid #10b981",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Appointment"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/DoctorEntryIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(16, 185, 129, 0.3)",
                    borderLeft: "3px solid #10b981",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Doctor Entry"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              {isAppointmentNeeded && (
                <ListItemButton
                  component={NavLink}
                  to="/WeeklySchedule"
                  sx={{
                    pl: open ? 4 : 2,
                    py: 1.2,
                    mx: 1,
                    borderRadius: "8px",
                    color: "rgba(255,255,255,0.9)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateX(4px)",
                    },
                    "&.active": {
                      bgcolor: "rgba(16, 185, 129, 0.3)",
                      borderLeft: "3px solid #10b981",
                      color: "white",
                    },
                  }}
                >
                  {open &&
                    <ListItemText
                      primary="Weekly Template"
                      primaryTypographyProps={{
                        style: {
                          marginLeft: '20px',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          fontSize: '13px',
                          fontWeight: 500,
                          letterSpacing: "0.2px",
                        },
                      }} />}
                </ListItemButton>
              )}

              {isAppointmentNeeded && (
                <ListItemButton
                  component={NavLink}
                  to="/DoctorMontlyDiary"
                  sx={{
                    pl: open ? 4 : 2,
                    py: 1.2,
                    mx: 1,
                    borderRadius: "8px",
                    color: "rgba(255,255,255,0.9)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateX(4px)",
                    },
                    "&.active": {
                      bgcolor: "rgba(16, 185, 129, 0.3)",
                      borderLeft: "3px solid #10b981",
                      color: "white",
                    },
                  }}
                >
                  {open &&
                    <ListItemText
                      primary="Doctor Diary"
                      primaryTypographyProps={{
                        style: {
                          marginLeft: '20px',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          fontSize: '13px',
                          fontWeight: 500,
                          letterSpacing: "0.2px",
                        },
                      }} />}
                </ListItemButton>
              )}

            </List>
          </Box>
        </Collapse>

        {
          hasReadAccountsPermission && (
            <ListItemButton
              onClick={() => setAccountsOpen(!accountsOpen)}
              sx={{
                borderRadius: "12px",
                mb: 0.5,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(102, 126, 234, 0.15)",
                  transform: "translateX(4px)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
                <AccountBalanceWalletIcon sx={{
                  color: "white",
                  fontSize: "22px",
                }} />
              </ListItemIcon>
              {open &&
                <ListItemText
                  primary="Accounts"
                  primaryTypographyProps={{
                    style: {
                      cursor: 'pointer',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    },
                  }}
                />}

              {open && (accountsOpen ?
                <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
                <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
              )}
            </ListItemButton>
          )
        }

        <Collapse in={accountsOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(245, 158, 11, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>

              <ListItemButton
                component={NavLink}
                to="/PurchaseIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(16, 185, 129, 0.3)",
                    borderLeft: "3px solid #10b981",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Purchase"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/IncomeExpenseIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(245, 158, 11, 0.3)",
                    borderLeft: "3px solid #f59e0b",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Income & Expense"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>
              
            </List>
          </Box>
        </Collapse>

        {
          hasReadAdministrationPermission && (
            <ListItemButton
              onClick={() => setUserOpen(!userOpen)}
              sx={{
                borderRadius: "12px",
                mb: 0.5,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(102, 126, 234, 0.15)",
                  transform: "translateX(4px)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
                <People sx={{
                  color: "white",
                  fontSize: "22px",
                }} />
              </ListItemIcon>
              {open &&
                <ListItemText
                  primary="Auth Zen"
                  primaryTypographyProps={{
                    style: {
                      cursor: 'pointer',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    },
                  }}
                />}
              {open && (userOpen ?
                <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
                <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
              )}
            </ListItemButton>

          )
        }

        <Collapse in={userOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(239, 68, 68, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>
              <ListItemButton
                component={NavLink}
                to="/UserIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(239, 68, 68, 0.3)",
                    borderLeft: "3px solid #ef4444",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Users"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/RoleIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(239, 68, 68, 0.3)",
                    borderLeft: "3px solid #ef4444",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Roles"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/PasswordChange"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(239, 68, 68, 0.3)",
                    borderLeft: "3px solid #ef4444",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Reset Password"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>
            </List>
          </Box>
        </Collapse>

      </List >
    </Drawer >

  );
};

export default Sidebar;
